import React, { useEffect, useState, useRef } from "react";
import { Plus, Search, Edit, Trash2, Upload, Image as ImageIcon, PackagePlus, Printer, BookOpen, X, GripVertical, LayoutGrid } from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Select, Label, Textarea } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatVND, formatDate, parseApiError} from "../lib/utils";
import { useSort } from "../hooks/useSort";
import { useFeature } from "../contexts/CreatorConfigContext";

const EMPTY = {
  name: "",
  sku: "",
  category: "Bánh bao",
  description: "",
  price: 0,
  wholesale_price: 0,
  cost: 0,
  stock: 0,
  low_stock_threshold: 10,
  unit: "cái",
  image_url: "",
  is_active: true,
  variants: [],
  expiration_days: 3,
};

const EMPTY_STOCK = {
  kind: "product",
  target_id: "",
  quantity: 0,
  unit_price: 0,
  supplier_id: "",
  supplier_name: "",
  production_date: new Date().toISOString().slice(0, 10),
  expiration_date: "",
  batch_code: "",
  notes: "",
};

const MAX_IMAGE_BYTES = 500 * 1024; // 500 KB

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Products() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("products.edit");
  const canDelete = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("products.delete");
  const canRecipe      = useFeature("products.recipe");
  const canProductPrint = useFeature("products.print");
  const canProductDelete = useFeature("products.delete");
  const canImageUpload = useFeature("products.image");
  const canSort        = useFeature("products.sort");
  const canCreate = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("products.create");
  const canStockIn = user?.role === "admin" || (user?.permissions_effective || user?.permissions || []).includes("stock.in");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [stockOpen, setStockOpen] = useState(false);
  const [stockForm, setStockForm] = useState(EMPTY_STOCK);
  const [stockInProduct, setStockInProduct] = useState(null); // sản phẩm đang nhập kho (để lấy recipe)
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [recipeProduct, setRecipeProduct] = useState(null);
  const [recipe, setRecipe] = useState([]); // [{material_id, material_name, quantity, unit}]
  const [recipeSaving, setRecipeSaving] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (lowOnly) params.low_stock = true;
    const { data } = await api.get("/products", { params });
    setItems(data);
  };

  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [category, lowOnly]);

  // Tìm kiếm theo q — debounce 300ms
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      sku: p.sku || "",
      category: p.category,
      description: p.description || "",
      price: p.price,
      cost: p.cost || 0,
      stock: p.stock || 0,
      low_stock_threshold: p.low_stock_threshold || 10,
      unit: p.unit || "cái",
      image_url: p.image_url || "",
      is_active: p.is_active !== false,
      variants: p.variants || [],
    });
    setOpen(true);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Vui lòng chọn file hình ảnh", variant: "error" });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast({
        title: "Ảnh quá lớn",
        description: `Tối đa ${Math.round(MAX_IMAGE_BYTES / 1024)} KB. Vui lòng nén ảnh trước.`,
        variant: "error",
      });
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setForm((prev) => ({ ...prev, image_url: b64 }));
      toast({ title: "Đã tải ảnh", variant: "success" });
    } catch {
      toast({ title: "Không đọc được file", variant: "error" });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/products/${editing.product_id}`, form);
      } else {
        await api.post("/products", form);
      }
      toast({ title: t("common.saved"), variant: "success" });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`${t("common.delete")} "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast({ title: t("common.deleted"), variant: "success" });
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const openRecipe = async (p) => {
    setRecipeProduct(p);
    try {
      const { data } = await api.get(`/products/${p.product_id}/recipe`);
      setRecipe(data.recipe || []);
    } catch {
      setRecipe([]);
    }
    setRecipeOpen(true);
  };

  const addRecipeRow = () => setRecipe((prev) => [...prev, { material_id: "", material_name: "", quantity: 1, unit: "" }]);

  const updateRecipeRow = (idx, field, value) => {
    setRecipe((prev) => {
      const next = [...prev];
      if (field === "material_id") {
        const mat = materials.find((m) => m.material_id === value);
        next[idx] = { ...next[idx], material_id: value, material_name: mat?.name || "", unit: mat?.unit || next[idx].unit };
      } else {
        next[idx] = { ...next[idx], [field]: value };
      }
      return next;
    });
  };

  const removeRecipeRow = (idx) => setRecipe((prev) => prev.filter((_, i) => i !== idx));

  const saveRecipe = async () => {
    if (!recipeProduct) return;
    setRecipeSaving(true);
    try {
      const validRecipe = recipe.filter((r) => r.material_id && r.quantity > 0);
      await api.put(`/products/${recipeProduct.product_id}/recipe`, { recipe: validRecipe });
      toast({ title: "Đã lưu công thức", variant: "success" });
      setRecipeOpen(false);
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    } finally {
      setRecipeSaving(false);
    }
  };

  const openStockIn = (p) => {
    setStockInProduct(p);
    setStockForm({
      ...EMPTY_STOCK,
      kind: "product",
      target_id: p.product_id,
      target_name: p.name,
      production_date: new Date().toISOString().slice(0, 10),
    });
    setStockOpen(true);
  };

  const submitStockIn = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        kind: "product",
        target_id: stockForm.target_id,
        quantity: Number(stockForm.quantity),
        unit_price: Number(stockForm.unit_price) || 0,
        supplier_id: stockForm.supplier_id || null,
        supplier_name: stockForm.supplier_name || null,
        production_date: stockForm.production_date || null,
        expiration_date: stockForm.expiration_date || null,
        batch_code: stockForm.batch_code || null,
        notes: stockForm.notes || null,
      };
      await api.post("/stock/in", payload);
      toast({ title: "Đã nhập kho", variant: "success" });
      setStockOpen(false);
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const { sorted, handleSort, SortIcon, sortKey } = useSort(items, "", "asc");
  const categories = Array.from(new Set(items.map((i) => i.category))).filter(Boolean);

  // ── Drag & Drop ─────────────────────────────────────────────
  const [dragMode, setDragMode] = useState(false);
  const [dragItems, setDragItems] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    if (dragMode) setDragItems([...sorted]);
  // eslint-disable-next-line
  }, [dragMode]);

  const handleDragStart = (e, productId) => {
    setDraggingId(productId);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, productId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (productId !== draggingId) setDragOverId(productId);
  };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setDragItems((prev) => {
      const next = [...prev];
      const fromIdx = next.findIndex((p) => p.product_id === draggingId);
      const toIdx = next.findIndex((p) => p.product_id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    setDragOverId(null);
    setDraggingId(null);
  };
  const handleDragEnd = () => { setDraggingId(null); setDragOverId(null); };
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.put("/products/reorder/save", { order: dragItems.map((p) => p.product_id) });
      toast({ title: "Đã lưu thứ tự sản phẩm!", variant: "success" });
      setDragMode(false);
      load();
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi lưu thứ tự"), variant: "error" });
    } finally {
      setSavingOrder(false);
    }
  };
  // ────────────────────────────────────────────────────────────


  const handlePrint = () => {
    const rows = sorted.map((p,i) => `<tr>
      <td>${i+1}</td><td>${p.name||''}</td>
      <td>${p.category||'—'}</td>
      <td style="text-align:right;font-family:monospace">${(p.price||0).toLocaleString('vi-VN')}₫</td>
      <td style="text-align:right;font-family:monospace">${(p.wholesale_price||0).toLocaleString('vi-VN')}₫</td>
      <td style="text-align:right">${p.stock||0}</td>
      <td>${p.is_active===false?'Ngừng':'Đang bán'}</td>
    </tr>`).join('');
    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
    <title>Danh sách sản phẩm</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left}
    th{background:#f0f0f0;font-size:11px;text-transform:uppercase}tr:nth-child(even) td{background:#fafafa}
    @media print{body{margin:10mm}}</style></head><body>
    <h2>Danh sách sản phẩm</h2>
    <p class="sub">${sorted.length} sản phẩm</p>
    <table><thead><tr><th>STT</th><th>Tên sản phẩm</th><th>Nhóm</th><th style="text-align:right">Giá lẻ</th><th style="text-align:right">Giá sỉ</th><th style="text-align:right">Tồn kho</th><th>Trạng thái</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <script>window.onload=()=>{window.print()}<\/script></body></html>`);
    win.document.close();
  };
  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("products.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">{items.length} {t("common.total").toLowerCase()}</p>
        </div>
        <Button onClick={openCreate} data-testid="products-new-button" className={!canCreate ? "hidden" : ""}>
          <Plus size={16} /> {t("common.add")}
        </Button>
      </div>

      <Card>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label>{t("common.search")}</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("products.name")} className="pl-8" data-testid="products-search" />
              </div>
            </div>
            <div>
              <Label>{t("products.category")}</Label>
              <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="products-category-filter">
                <option value="">{t("common.all")}</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} data-testid="products-low-stock-filter" />
                {t("products.lowStockOnly")}
              </label>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Thanh sắp xếp + nút drag mode */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {!dragMode ? (
          <div className="flex items-center gap-2 flex-wrap text-xs text-ink-muted">
            <span className="font-medium">Sắp xếp:</span>
            {[["name","Tên"],["price","Giá"],["stock","Tồn kho"],["category","Danh mục"]].map(([key,label]) => (
              <button key={key} onClick={() => handleSort(key)}
                className={`px-2 py-1 rounded border transition-colors ${sortKey===key ? "border-bamboo text-bamboo bg-bamboo/5" : "border-border hover:border-bamboo/50"}`}>
                {label}{SortIcon(key)}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-bamboo font-medium animate-pulse">
            <GripVertical size={16} />
            <span>Kéo thả để sắp xếp vị trí sản phẩm</span>
          </div>
        )}
        {canEdit && canSort && (
          <div className="flex items-center gap-2">
            {dragMode ? (
              <>
                <button onClick={() => setDragMode(false)}
                  className="px-3 py-1.5 rounded border border-border text-xs text-ink-muted hover:border-red-300 hover:text-red-600 transition-colors">
                  Hủy
                </button>
                <button onClick={saveOrder} disabled={savingOrder}
                  className="px-3 py-1.5 rounded bg-bamboo text-white text-xs font-semibold hover:bg-bamboo/90 transition-colors disabled:opacity-60">
                  {savingOrder ? "Đang lưu..." : "💾 Lưu thứ tự"}
                </button>
              </>
            ) : (
              <button onClick={() => setDragMode(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-border text-xs text-ink-muted hover:border-bamboo hover:text-bamboo transition-colors">
                <LayoutGrid size={13} /> Sắp xếp tự do
              </button>
            )}
          </div>
        )}
      </div>

      {/* GRID SẢN PHẨM */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
        {(dragMode ? dragItems : sorted).length === 0 ? (
          <div className="col-span-full text-center py-12 text-ink-muted">{t("common.empty")}</div>
        ) : (dragMode ? dragItems : sorted).map((p) => {
          const lowStock = p.stock <= p.low_stock_threshold;
          const negative = p.stock < 0;
          const isDragging = draggingId === p.product_id;
          const isDragOver = dragOverId === p.product_id;
          return (
            <Card
              key={p.product_id}
              draggable={dragMode}
              onDragStart={dragMode ? (e) => handleDragStart(e, p.product_id) : undefined}
              onDragOver={dragMode ? (e) => handleDragOver(e, p.product_id) : undefined}
              onDrop={dragMode ? (e) => handleDrop(e, p.product_id) : undefined}
              onDragEnd={dragMode ? handleDragEnd : undefined}
              className={[
                "overflow-hidden transition-all",
                dragMode ? "cursor-grab active:cursor-grabbing select-none" : "hover:-translate-y-0.5 hover:shadow-md",
                isDragging ? "opacity-40 scale-95 shadow-none border-dashed border-bamboo" : "",
                isDragOver ? "ring-2 ring-bamboo ring-offset-1 scale-[1.02]" : "",
              ].filter(Boolean).join(" ")}
              data-testid={`product-card-${p.product_id}`}
            >
              <div className="aspect-[4/3] bg-cream relative overflow-hidden">
                {dragMode && (
                  <div className="absolute inset-0 bg-bamboo/0 hover:bg-bamboo/5 transition-colors flex items-start justify-start p-2 z-10">
                    <GripVertical size={18} className="text-bamboo/60 drop-shadow" />
                  </div>
                )}
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-muted text-xs">no image</div>
                )}
                {!dragMode && (lowStock || negative) && (
                  <div className="absolute top-2 right-2">
                    <Badge variant={negative ? "cancelled" : p.stock === 0 ? "cancelled" : "lowstock"}>
                      {negative ? `Âm ${p.stock}` : p.stock === 0 ? t("reports.outOfStock") : t("products.lowStockOnly")}
                    </Badge>
                  </div>
                )}
              </div>
              <CardContent className="space-y-2 !p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-ink truncate">{p.name}</div>
                    <div className="text-xs text-ink-muted">{p.category}</div>
                  </div>
                  <div className="font-mono text-bamboo font-bold whitespace-nowrap">{formatVND(p.price)}</div>
                </div>
                {!dragMode && (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-ink-muted">
                        {t("products.stock")}:{" "}
                        <span className={`font-medium ${negative ? "text-red-700" : lowStock ? "text-red-600" : "text-ink"}`}>
                          {p.stock}
                        </span>
                        <span className="text-ink-muted">/{p.low_stock_threshold} {p.unit}</span>
                      </span>
                      <div className="flex gap-1">
                        {canEdit && canRecipe && (
                          <Button variant="ghost" size="icon" onClick={() => openRecipe(p)} title="Công thức NVL" data-testid={`product-recipe-${p.product_id}`}>
                            <BookOpen size={14} className={p.recipe?.length ? "text-amber-600" : "text-ink-muted"} />
                          </Button>
                        )}
                        {canStockIn && (
                          <Button variant="ghost" size="icon" onClick={() => openStockIn(p)} data-testid={`product-stockin-${p.product_id}`} title="Nhập kho">
                            <PackagePlus size={14} className="text-bamboo" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`product-edit-${p.product_id}`}>
                            <Edit size={14} />
                          </Button>
                        )}
                        {canDelete && canProductDelete && (
                          <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`product-delete-${p.product_id}`}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {p.latest_expiration_date && (
                      <div className="text-[11px] text-ink-muted mt-1 flex items-center gap-1">
                        HSD lô gần nhất: <span className="font-medium text-bamboo">{formatDate(p.latest_expiration_date)}</span>
                      </div>
                    )}
                    {p.recipe?.length > 0 && (
                      <div className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5">
                        <BookOpen size={10} /> Công thức: {p.recipe.length} nguyên liệu
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? t("common.edit") : t("common.add")} size="lg" testId="product-modal">
        <form onSubmit={submit} className="space-y-4">
          {/* Image upload area */}
          <div>
            <Label>{t("products.image")}</Label>
            <div className="flex items-start gap-4">
              <div className="w-28 h-28 rounded-md border border-border bg-cream/50 overflow-hidden flex items-center justify-center shrink-0">
                {form.image_url ? (
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={32} className="text-ink-muted" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickFile}
                  className="hidden"
                  data-testid="product-form-image-file"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="product-form-upload-button" style={canImageUpload ? {} : {display:'none'}}>
                  <Upload size={14} /> Tải ảnh từ máy
                </Button>
                <div className="text-xs text-ink-muted">Hoặc dán URL ảnh:</div>
                <Input
                  value={form.image_url?.startsWith("data:") ? "" : form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-form-image-url"
                />
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Xóa ảnh
                  </button>
                )}
                <div className="text-[11px] text-ink-muted">Tối đa 500KB. JPG/PNG/WebP.</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("products.name")} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-form-name" />
            </div>
            <div>
              <Label>{t("products.category")}</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category" />
            </div>
            <div>
              <Label>{t("common.price")} (VND) *</Label>
              <Input type="number" min={0} value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} required data-testid="product-form-price" />
            </div>
            <div>
              <Label>{t("products.cost")} (VND)</Label>
              <Input type="number" min={0} value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} />
            </div>
            <div>
              <Label>{t("products.stock")}</Label>
              <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} data-testid="product-form-stock" />
            </div>
            <div>
              <Label>{t("products.lowStockThreshold")}</Label>
              <Input type="number" min={0} value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: Number(e.target.value) })} />
            </div>
            <div>
              <Label>{t("products.unit")}</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </div>
            <div>
              <Label>HSD mặc định (ngày)</Label>
              <Input type="number" min={1} value={form.expiration_days} onChange={(e) => setForm({ ...form, expiration_days: Number(e.target.value) })} placeholder="3" data-testid="product-form-expiration-days" />
            </div>
            <div>
              <Label>Giá sỉ (VND)</Label>
              <Input type="number" min={0} value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label>{t("products.description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="product-form-submit">{t("common.save")}</Button>
          </div>
        </form>
      </Modal>

      {/* Stock-in for product (nhập kho ngày mới) */}
      <Modal open={stockOpen} onClose={() => setStockOpen(false)} title={`Nhập kho sản phẩm: ${stockForm.target_name || ""}`} size="lg" testId="product-stockin-modal">
        <form onSubmit={submitStockIn} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Số lượng *</Label>
              <Input type="number" min={1} required value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: Number(e.target.value) })} data-testid="product-stockin-qty" />
            </div>
            <div>
              <Label>Đơn giá (nếu có)</Label>
              <Input type="number" min={0} value={stockForm.unit_price} onChange={(e) => setStockForm({ ...stockForm, unit_price: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Ngày sản xuất</Label>
              <Input type="date" value={stockForm.production_date} onChange={(e) => setStockForm({ ...stockForm, production_date: e.target.value })} data-testid="product-stockin-prod-date" />
            </div>
            <div>
              <Label>HSD</Label>
              <Input type="date" value={stockForm.expiration_date} onChange={(e) => setStockForm({ ...stockForm, expiration_date: e.target.value })} placeholder="Tự tính" data-testid="product-stockin-exp-date" />
            </div>
            <div className="col-span-2">
              <Label>Nhà cung cấp (tùy chọn)</Label>
              <Select value={stockForm.supplier_id} onChange={(e) => {
                const s = suppliers.find((x) => x.supplier_id === e.target.value);
                setStockForm({ ...stockForm, supplier_id: e.target.value, supplier_name: s?.name || "" });
              }} data-testid="product-stockin-supplier">
                <option value="">— Chọn NCC —</option>
                {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
              </Select>
              {stockForm.supplier_id && stockForm.unit_price > 0 && (
                <div className="text-xs text-amber-700 mt-1">💡 Sẽ tự ghi nhận <strong>{formatVND(stockForm.unit_price * stockForm.quantity)}</strong> vào công nợ NCC</div>
              )}
            </div>
            <div className="col-span-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} />
            </div>
          </div>
          {/* Preview NVL sẽ bị trừ theo công thức */}
          {(() => {
            const recipe = stockInProduct?.recipe || [];
            const qty = Number(stockForm.quantity) || 0;
            const rows = recipe.filter((r) => r.material_id && r.quantity > 0);
            if (rows.length === 0 || qty === 0) return null;
            return (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <span>⚠️</span>
                  <span>Khi nhập kho <strong>{qty} {stockInProduct?.unit || "cái"}</strong>, hệ thống sẽ tự động trừ NVL:</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-amber-700 border-b border-amber-200">
                      <th className="text-left py-1 font-semibold">Nguyên liệu</th>
                      <th className="text-right py-1 font-semibold">Công thức / cái</th>
                      <th className="text-right py-1 font-semibold text-red-700">Tổng trừ</th>
                      <th className="text-right py-1 font-semibold">Tồn hiện tại</th>
                      <th className="text-right py-1 font-semibold">Còn lại sau</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const mat = materials.find((m) => m.material_id === r.material_id);
                      const tru = parseFloat((r.quantity * qty).toFixed(4));
                      const ton = parseFloat((mat?.stock ?? 0).toFixed(4));
                      const conlai = parseFloat((ton - tru).toFixed(4));
                      const thieu = conlai < 0;
                      return (
                        <tr key={i} className={`border-b border-amber-100 ${thieu ? "bg-red-50" : ""}`}>
                          <td className="py-1.5 font-medium text-ink">{mat?.name || r.material_name}</td>
                          <td className="py-1.5 text-right font-mono text-ink-muted">
                            {r.quantity} <span className="text-ink-muted">{r.unit || mat?.unit}</span>
                          </td>
                          <td className="py-1.5 text-right font-mono font-bold text-red-600">
                            −{tru} <span className="font-normal">{r.unit || mat?.unit}</span>
                          </td>
                          <td className="py-1.5 text-right font-mono text-ink-muted">
                            {ton} <span className="text-ink-muted">{r.unit || mat?.unit}</span>
                          </td>
                          <td className={`py-1.5 text-right font-mono font-bold ${thieu ? "text-red-700" : "text-green-700"}`}>
                            {thieu ? <span title="Không đủ NVL!">⚠️ {conlai}</span> : conlai}
                            {" "}<span className="font-normal text-xs">{r.unit || mat?.unit}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.some((r) => {
                  const mat = materials.find((m) => m.material_id === r.material_id);
                  return (mat?.stock ?? 0) - r.quantity * qty < 0;
                }) && (
                  <div className="text-xs text-red-700 font-semibold flex items-center gap-1">
                    ⚠️ Một số nguyên liệu không đủ tồn kho. Vẫn có thể nhập kho nhưng tồn NVL sẽ âm.
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setStockOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="product-stockin-submit">Nhập kho</Button>
          </div>
        </form>
      </Modal>

      {/* Modal công thức nguyên liệu */}
      <Modal open={recipeOpen} onClose={() => setRecipeOpen(false)} title={`Công thức NVL: ${recipeProduct?.name || ""}`} size="lg" testId="recipe-modal">
        <div className="space-y-3">
          <div className="text-sm text-ink-muted bg-amber-50 border border-amber-200 rounded-lg p-3">
            💡 Nhập công thức nguyên liệu cho <strong>1 {recipeProduct?.unit || "cái"}</strong>. Hệ thống sẽ tự động trừ NVL khi tạo lô sản xuất.
          </div>

          <div className="space-y-2">
            {recipe.length === 0 && (
              <div className="text-center py-6 text-ink-muted text-sm">Chưa có công thức. Nhấn "+ Thêm NVL" để bắt đầu.</div>
            )}
            {recipe.map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-5">
                  <Select value={row.material_id} onChange={(e) => updateRecipeRow(i, "material_id", e.target.value)}>
                    <option value="">— Chọn NVL —</option>
                    {materials.map((m) => (
                      <option key={m.material_id} value={m.material_id}>{m.name} ({m.unit})</option>
                    ))}
                  </Select>
                </div>
                <div className="col-span-3">
                  <Input
                    type="number" min={0} step="any"
                    value={row.quantity}
                    onChange={(e) => updateRecipeRow(i, "quantity", Number(e.target.value))}
                    placeholder="Số lượng"
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    value={row.unit}
                    onChange={(e) => updateRecipeRow(i, "unit", e.target.value)}
                    placeholder="ĐVT (kg, g, ...)"
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeRecipeRow(i)}>
                    <X size={14} className="text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <span className="font-medium">Nguyên liệu</span>
            <span>|</span>
            <span>Số lượng / {recipeProduct?.unit || "cái"}</span>
            <span>|</span>
            <span>Đơn vị</span>
          </div>

          {/* Summary nếu có công thức */}
          {recipe.filter((r) => r.material_id && r.quantity > 0).length > 0 && (
            <div className="bg-cream border border-border rounded-lg p-3 text-xs space-y-1">
              <div className="font-semibold text-ink mb-2">Xem trước (sản xuất 100 {recipeProduct?.unit || "cái"}):</div>
              {recipe.filter((r) => r.material_id && r.quantity > 0).map((r, i) => {
                const mat = materials.find((m) => m.material_id === r.material_id);
                return (
                  <div key={i} className="flex justify-between">
                    <span>{mat?.name || r.material_name}</span>
                    <span className="font-mono">{(r.quantity * 100).toFixed(2)} {r.unit || mat?.unit}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button type="button" variant="outline" size="sm" onClick={addRecipeRow}>
              + Thêm NVL
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" type="button" onClick={() => setRecipeOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={saveRecipe} disabled={recipeSaving}>
                {recipeSaving ? "Đang lưu..." : t("common.save")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}